/* iter-82 — Training Creator dialog.

Drops into any UI surface that wants to launch an AI-powered training
pack generator. Used from the HR · Training workspace tile (Phase 1)
and any future trigger.

Pipeline: collects topic / audience / duration / module count / depth
→ POST /api/admin/ai/generate-training → server returns the full
Markdown training pack AND persists it as a workspace_documents entry
under hr-projects.training so the new training appears in the
workspace tree immediately. */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, X, GraduationCap } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_PRESETS = [
  'front-line team',
  'supervisors',
  'managers',
  'department heads',
  'new hires',
  'cross-departmental',
];
const DEPTH_PRESETS = [
  { id: 'quick',    label: 'Quick · 30-45 min',   default_min: 30 },
  { id: 'standard', label: 'Standard · 60-90 min', default_min: 90 },
  { id: 'deep',     label: 'Deep · 2-4 hours',    default_min: 180 },
];

export default function TrainingCreatorButton({ onCreated, className = '', label = 'Create Training (AI)' }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    topic: '',
    audience: 'front-line team',
    depth: 'standard',
    duration_minutes: 90,
    module_count: 4,
    constraints: '',
  });
  const [result, setResult] = useState(null);

  const reset = () => {
    setForm({ topic: '', audience: 'front-line team', depth: 'standard', duration_minutes: 90, module_count: 4, constraints: '' });
    setResult(null);
  };

  const setDepth = (id) => {
    const preset = DEPTH_PRESETS.find((d) => d.id === id);
    setForm((f) => ({ ...f, depth: id, duration_minutes: preset?.default_min || f.duration_minutes }));
  };

  const generate = async () => {
    if (!form.topic.trim()) {
      toast.error('Topic is required.');
      return;
    }
    setBusy(true);
    const startToast = toast.loading('Designing your training pack with GPT-5.2 — this takes 30-90 seconds…');
    try {
      // iter-83 — Default-async: server returns {job_id, status:'pending'};
      // poll /jobs/{id} every 4s until status flips. This avoids the
      // ingress 60-120s timeout that previously surfaced as a 502.
      const { data: kickoff } = await axios.post(
        `${API}/admin/ai/generate-training`,
        {
          topic: form.topic.trim(),
          audience: form.audience,
          depth: form.depth,
          duration_minutes: parseInt(form.duration_minutes, 10) || 90,
          module_count: parseInt(form.module_count, 10) || 4,
          constraints: form.constraints.trim() || null,
          save_to_workspace: true,
          async_mode: true,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );

      const jobId = kickoff?.job_id;
      if (!jobId) {
        // Server returned the legacy sync payload — render directly.
        toast.dismiss(startToast);
        toast.success(`Training pack ready — ${kickoff.word_count?.toLocaleString?.() || '?'} words saved to HR · Training.`);
        setResult(kickoff);
        onCreated?.(kickoff);
        return;
      }

      // Poll for up to ~6 minutes (90 polls × 4s) before giving up.
      let pollResult = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: status } = await axios.get(
          `${API}/admin/ai/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
        );
        if (status?.status === 'done') {
          pollResult = status.result;
          break;
        }
        if (status?.status === 'error') {
          throw new Error(status.error || 'Training generation failed.');
        }
      }
      if (!pollResult) throw new Error('Training generation timed out after 6 minutes.');

      toast.dismiss(startToast);
      toast.success(`Training pack ready — ${pollResult.word_count.toLocaleString()} words saved to HR · Training.`);
      setResult(pollResult);
      onCreated?.(pollResult);
    } catch (e) {
      toast.dismiss(startToast);
      toast.error(e?.response?.data?.detail || e?.message || 'Training generation failed.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="training-creator-trigger"
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white ${className}`}
        style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#EC4899 100%)', fontFamily: 'Manrope' }}
        title="Generate a complete multi-module training pack with GPT-5.2"
      >
        <GraduationCap className="w-3.5 h-3.5" />
        {label}
      </button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-xl w-[95vw] p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="training-creator-dialog">
          <DialogTitle className="sr-only">Create training pack with AI</DialogTitle>
          <DialogDescription className="sr-only">
            Generate a complete deliverable training pack from a topic brief using GPT-5.2.
          </DialogDescription>

          <div className="px-5 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#EC4899 100%)', fontFamily: 'Manrope' }}>
            <GraduationCap className="w-5 h-5 text-white" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#FCE7F3' }}>Atrium AI · HR</p>
              <p className="text-sm font-bold text-white">Training Pack Generator</p>
            </div>
            <button onClick={() => setOpen(false)} type="button" aria-label="Close" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" data-testid="training-creator-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!result && (
            <div className="p-5 space-y-4" style={{ fontFamily: 'Manrope' }}>
              <Field label="Topic *">
                <textarea
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  data-testid="training-topic"
                  rows={2}
                  autoFocus
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                  style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
                  placeholder='e.g. "Brand standards — Concierge handoff at check-in"'
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Audience">
                  <select
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value })}
                    data-testid="training-audience"
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                    style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
                  >
                    {AUDIENCE_PRESETS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
                <Field label="Module count">
                  <input
                    type="number"
                    min={2}
                    max={8}
                    value={form.module_count}
                    onChange={(e) => setForm({ ...form, module_count: e.target.value })}
                    data-testid="training-module-count"
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                    style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
                  />
                </Field>
              </div>

              <Field label="Depth">
                <div className="grid grid-cols-3 gap-2">
                  {DEPTH_PRESETS.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDepth(d.id)}
                      data-testid={`training-depth-${d.id}`}
                      className="px-3 py-2 rounded-lg text-[11px] font-bold border"
                      style={{
                        borderColor: form.depth === d.id ? '#8B5CF6' : '#D9E3F1',
                        background: form.depth === d.id ? '#F5F3FF' : 'white',
                        color: form.depth === d.id ? '#8B5CF6' : '#4A5D5D',
                      }}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Duration (minutes)">
                <input
                  type="number"
                  min={15}
                  max={480}
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  data-testid="training-duration"
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                  style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
                />
              </Field>

              <Field label="Additional constraints (optional)">
                <textarea
                  value={form.constraints}
                  onChange={(e) => setForm({ ...form, constraints: e.target.value })}
                  data-testid="training-constraints"
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                  style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
                  placeholder='e.g. "Must reference Hyatt Inclusive brand standards section 4.2"'
                />
              </Field>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-lg text-xs font-bold"
                  style={{ background: '#E5E7EB', color: '#374151' }}
                >Cancel</button>
                <button
                  onClick={generate}
                  disabled={busy || !form.topic.trim()}
                  data-testid="training-generate"
                  className="px-5 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 inline-flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#EC4899 100%)' }}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Generate training pack
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="p-5 space-y-3" style={{ fontFamily: 'Manrope' }}>
              <div className="p-3 rounded-lg border" style={{ borderColor: '#10B98155', background: '#ECFDF5' }}>
                <p className="text-xs font-bold" style={{ color: '#059669' }}>✓ Training saved to HR · Training</p>
                <p className="text-[11px] mt-1" style={{ color: '#4A5D5D' }}>
                  <strong>{result.title}</strong> · {result.word_count.toLocaleString()} words · {result.duration_minutes} min · {result.module_count} modules
                </p>
              </div>
              <div className="max-h-[420px] overflow-y-auto p-4 rounded-lg border" style={{ borderColor: '#D9E3F1', background: '#FAFAFA' }} data-testid="training-result-markdown">
                <pre className="whitespace-pre-wrap text-[11px] leading-relaxed" style={{ color: '#1A2E2E', fontFamily: 'ui-monospace, monospace' }}>
                  {result.markdown}
                </pre>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { reset(); }}
                  data-testid="training-create-another"
                  className="px-4 py-2 rounded-lg text-xs font-bold"
                  style={{ background: '#E5E7EB', color: '#374151' }}
                >Create another</button>
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#EC4899 100%)' }}
                >Done</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>{label}</p>
      {children}
    </div>
  );
}
